import { API_CONSTANTS } from '../../constants/api-constants';

// RFC 8693 token-exchange + RFC 6749 refresh, as implemented by maia-auth
// (`/oauth/token`). These literals are the broker's exact wire contract — see
// the slice-4 maia-auth handback.
const TOKEN_EXCHANGE_GRANT_TYPE =
  'urn:ietf:params:oauth:grant-type:token-exchange';
const ACCESS_TOKEN_SUBJECT_TYPE =
  'urn:ietf:params:oauth:token-type:access_token';
const REFRESH_TOKEN_GRANT_TYPE = 'refresh_token';
const FORM_CONTENT_TYPE = 'application/x-www-form-urlencoded';

export interface BrokerTokens {
  accessToken: string;
  refreshToken: string;
  expiresInSeconds: number;
}

// The broker's `{ error, error_description }` envelope. `invalid_grant` covers
// every denial except `user_not_provisioned`, which carries a distinct
// `error_description` the SPA can render specifically.
export class BrokerError extends Error {
  constructor(
    public readonly code: string,
    description: string,
  ) {
    super(description);
    this.name = 'BrokerError';
  }
}

function brokerUrl(path: string): string {
  return `${API_CONSTANTS.BROKER_URL}${path}`;
}

async function postToken(
  params: Record<string, string>,
): Promise<BrokerTokens> {
  const response = await fetch(brokerUrl('/oauth/token'), {
    method: 'POST',
    headers: { 'Content-Type': FORM_CONTENT_TYPE },
    body: new URLSearchParams(params).toString(),
  });

  const data = (await response.json().catch(() => ({}))) as {
    access_token?: string;
    refresh_token?: string;
    expires_in?: number;
    error?: string;
    error_description?: string;
  };

  if (!response.ok || !data.access_token || !data.refresh_token) {
    throw new BrokerError(
      typeof data.error === 'string' ? data.error : 'invalid_grant',
      typeof data.error_description === 'string'
        ? data.error_description
        : 'broker token request failed',
    );
  }

  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresInSeconds: data.expires_in ?? 0,
  };
}

// Login: exchange the Auth0 access token (minted for the broker audience) for a
// Maia session scoped to the user's default tenant.
export function exchangeAuth0Token(
  auth0BrokerAudienceToken: string,
): Promise<BrokerTokens> {
  return postToken({
    grant_type: TOKEN_EXCHANGE_GRANT_TYPE,
    subject_token: auth0BrokerAudienceToken,
    subject_token_type: ACCESS_TOKEN_SUBJECT_TYPE,
  });
}

// Tenant switch: re-mint a Maia session for `requestedTenant` using a live Maia
// access token as the subject — no Auth0 round-trip. The broker creates a new,
// independent session (its own refresh lineage) for that tenant.
export function exchangeForTenant(
  currentMaiaAccessToken: string,
  requestedTenant: string,
): Promise<BrokerTokens> {
  return postToken({
    grant_type: TOKEN_EXCHANGE_GRANT_TYPE,
    subject_token: currentMaiaAccessToken,
    subject_token_type: ACCESS_TOKEN_SUBJECT_TYPE,
    requested_tenant: requestedTenant,
  });
}

// Single-use refresh-token rotation. The response carries the next refresh
// token; the caller must persist it and discard the old one.
export function refreshTokens(refreshToken: string): Promise<BrokerTokens> {
  return postToken({
    grant_type: REFRESH_TOKEN_GRANT_TYPE,
    refresh_token: refreshToken,
  });
}

// RFC 7009 revoke. Always 200; revoking an unknown/already-revoked token is a
// no-op, so failures here never block logout.
export async function revokeTokens(refreshToken: string): Promise<void> {
  await fetch(brokerUrl('/oauth/revoke'), {
    method: 'POST',
    headers: { 'Content-Type': FORM_CONTENT_TYPE },
    body: new URLSearchParams({ token: refreshToken }).toString(),
  });
}
